import {
  isAllowedByRoles,
  type RoleGateUser,
} from '@/components/Guard/roleGate';
import type { Role } from '@/shared/types';

export const TAGS = ['ems', 'ca', 'compliance', 'ca-lead'] as const;
export type Tag = (typeof TAGS)[number];

/** One line per agent for the composer's `@` list (OMT-1776). */
export const TAG_DESCRIPTIONS: Record<Tag, string> = {
  ems: 'Shortlists lenders for this client',
  ca: 'Writes the broker and compliance notes',
  compliance: 'Reviews the pack independently',
  'ca-lead': 'Suggests a CA for this assessment',
};

/** The agent id the service uses for each tag (`agents.yaml` key): chat rows and the Agents tab name it so. */
export const TAG_AGENTS: Record<Tag, string> = {
  ems: 'ems',
  ca: 'ca',
  compliance: 'compliance',
  'ca-lead': 'ca_lead',
};

/**
 * Tags only some roles may use (OMT-1846); every other tag is open to all. This
 * only decides what the `@` list offers: the service refuses `@ca-lead` from
 * anyone else, and always lets its own superadmins through.
 */
const TAG_ROLES: Partial<Record<Tag, Role[]>> = {
  'ca-lead': ['admin', 'mortgage_ca_lead'],
};

/**
 * The tags this user is offered in the composer. A superuser sees them all
 * (the `isAllowedByRoles` bypass); with no profile yet, only the open ones.
 */
export const tagsFor = (user: RoleGateUser | null | undefined): Tag[] =>
  TAGS.filter((tag) => {
    const roles = TAG_ROLES[tag];
    return !roles || isAllowedByRoles(user, roles);
  });

const OPEN_TAGS = tagsFor(null);

/** Puts `@tag ` at the start of the draft, replacing a leading tag. */
export const withTag = (draft: string, tag: string): string =>
  `@${tag} ${draft.replace(/^@[A-Za-z0-9_-]+\s*/, '')}`;

/** Whether `search` (the text typed after `@`) starts the tag, ignoring case. */
export const tagMatches = (tag: string, search: string): boolean =>
  tag.startsWith(search.toLowerCase());

/**
 * Whether the composer's `@` list may open. The server only routes on a leading
 * tag, so the draft's only `@` must be its first character (then it is the one
 * rc-mentions measured from); the search must have no space yet and match one
 * of `tags`, the list this user is offered (`tagsFor`). Without it, only the
 * tags open to everyone count.
 */
export const isTagSearch = (
  draft: string,
  search: string,
  tags: readonly Tag[] = OPEN_TAGS,
): boolean =>
  draft.lastIndexOf('@') === 0 &&
  !/\s/.test(search) &&
  tags.some((tag) => tagMatches(tag, search));
